'use client';
import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useUser } from '@/lib/UserContext';

const footerLinks = {
  Platform: [
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Leaderboard', href: '/leaderboard' },
    { label: 'Affiliate Program', href: '/affiliate' },
    { label: 'Payments & Withdrawals', href: '/payments' },
  ],
  Account: [
    { label: 'Sign In', href: '/dashboard' },
    { label: 'Get Started', href: '/payment-screen' },
    { label: 'Certificate Verify', href: '#' },
    { label: 'Payout Request', href: '#' },
    { label: 'Trading Rules', href: '/rules', requirePaid: true },
  ],
  Legal: [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Risk Disclosure', href: '/risk-disclosure' },
    { label: 'Refund Policy', href: '/refund-policy' },
  ],
};

function TwitterIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function InstagramIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  );
}

function TelegramIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

export default function HomepageFooter() {
  const { user } = useUser();

  // Filter footer links based on payment status
  const filteredFooterLinks = useMemo(() => {
    const hasPaid = user?.hasPaid ?? false;
    const filtered: { [key: string]: Array<{ label: string; href: string; requirePaid?: boolean }> } = {};

    for (const [category, links] of Object.entries(footerLinks)) {
      filtered[category] = links.filter(link => !link.requirePaid || hasPaid);
    }

    return filtered;
  }, [user]);

  return (
    <footer className="border-t border-border bg-card/30">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="xl:col-span-2 space-y-5">
            <img
              src="/assets/images/chatgpt-footer-image.png"
              alt="HiyaPrime"
              className="w-full max-w-xs rounded-lg"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              HiyaPrime provides retail traders with funded account access through transparent evaluation challenges.
            </p>
            <div className="flex items-center gap-3">
              {[
                { id: 'social-twitter', icon: TwitterIcon, label: 'Twitter', href: 'https://x.com/hiyaprime' },
                { id: 'social-instagram', icon: InstagramIcon, label: 'Instagram', href: 'https://instagram.com/hiyaprime' },
                { id: 'social-telegram', icon: TelegramIcon, label: 'Telegram', href: 'https://t.me/hiyaprime' },
              ]?.map((s) => (
                <a
                  key={s?.id}
                  href={s?.href}
                  aria-label={s?.label}
                  className="w-9 h-9 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
                >
                  <s.icon size={15} />
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ExternalLink size={11} />
              <a href="#" className="hover:text-primary transition-colors">Verify Certificate</a>
            </div>
            <div className="pt-4 border-t border-border/50">
              <p
                className="text-sm leading-loose font-light tracking-wide"
                style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'serif' }}
              >
                如果它吃掉你所有的精力，它可能不会让你赚很多钱；如果它让你赚很多钱，它可能不会吃掉你所有的精力。
              </p>
            </div>
          </div>

          {/* Links */}
          {Object.entries(filteredFooterLinks)?.map(([category, links]) => (
            links.length > 0 && (
              <div key={`footer-col-${category}`} className="space-y-4">
                <div className="text-xs font-semibold text-foreground uppercase tracking-wider">{category}</div>
                <ul className="space-y-2.5">
                  {links?.map((link) => (
                    <li key={`footer-link-${link?.label}`}>
                      <a
                        href={link?.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link?.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border space-y-6">
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">About:</strong> Simulated trading operations are managed by HiyaPrime Limited. HiyaPrime provides services of simulated trading and educational tools for skill assessment and enhancement of traders. HiyaPrime does not act as a broker and does not accept any deposits. Any purchases made should not be regarded as deposits. There are no promises of rewards or returns.
            </p>
            
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">Risk Disclosure:</strong> All of the information provided on this website and by HiyaPrime Ltd, or its affiliates, is intended solely for Educational purposes. Nothing on this website is to be construed as investment advice, nor an offer or invitation to buy or sell any financial instrument, nor does it endorse, recommend, or sponsor any financial product, company, or fund. Testimonials on the Company's website may not be reflective of the experience of other clients or customers and should not be considered as an assurance of future performance or success. HiyaPrime only provides services of simulated trading and educational tools for skill assessment and enhancement of traders. Trading in financial markets is inherently high-risk and speculative. The content and information provided on this website are not intended for distribution to, or use by, any person or entity in any jurisdiction or country where such distribution or use would be contrary to local law or regulation.
            </p>

            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">Hypothetical Performance Disclosure:</strong> Hypothetical performance results have many inherent limitations. No representation is being made that any account will or is likely to achieve profits or losses similar to those shown. In fact, there are frequently sharp differences between hypothetical performance results and the actual results subsequently achieved by any particular trading program. One of the limitations of hypothetical performance results is that they are generally prepared with the benefit of hindsight. In addition, hypothetical trading does not involve financial risk, and no hypothetical trading record can completely account for the impact of financial risk of actual trading. Testimonials appearing on this website may not be representative of other clients or customers and are not a guarantee of future performance or success.
            </p>

            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">Evaluation Disclosure:</strong> The customer pass rate of the Challenge/Evaluation program was 35% between 10th November, 2024 – 29th May, 2025, who traded at least one evaluation and obtained a HiyaPrime (Sim. Funded) Account during this time period. The Challenge and HiyaPrime (Sim. Funded) Account are meant to be a realistic simulation of trading under actual market conditions, including commissions, to mimic real market conditions, as much as possible. The evaluation is difficult to pass even for experienced traders. The Evaluation is not suggested for individuals with little to no trading experience.
            </p>

            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">Customer Compensation Disclosure:</strong> All trades presented for compensation to customers should be considered hypothetical and should not be expected to be replicated in a live trading account. HiyaPrime (Sim. Funded) Account may represent simulated accounts or live or copied accounts. HiyaPrime does not provide services to the residents of certain countries including – Afghanistan, Belarus, Burundi, China, Cuba, Congo, Sudan, Sri Lanka, North Korea (Democratic People's Republic of Korea) and Yemen.
            </p>

            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-warning">Website Authenticity:</strong> This is the only website for HiyaPrime. We are not using any third party websites or links. Any link, outside of this website that claims to be ours, could be fraudulent and users are advised to not use it.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <span>© 2026 HiyaPrime Trading Firm. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <a href="/cookies" className="hover:text-foreground transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
